import * as React from 'react';
import { createPortal } from 'react-dom';
import { debounce, memoizeFunction } from '@agilepoint/utils-common';
import { ListIterator } from '../../utils/array';
import { isHorizontal } from './utils';
import { ITabsPanelProps, ITabItemInfoBase, ITabItemProps, ITabItemInfoProps } from './tab-types';
import {
    TabPanelWrapper, TabContentContainer, TabHeaderContainer
} from './parts';
import { TabContext, TabDataProvider, TabIdProvider } from './tab-context';
import { clamp } from '../../utils/math';

export interface ITabState {
    activeTab: React.Key;
    tabOverFlow: boolean;
}

export interface ITabItemBaseProps extends ITabItemInfoBase, ITabItemInfoProps, ITabItemProps { }

export class TabsPanel extends React.PureComponent<ITabsPanelProps, ITabState> {

    static defaultProps: Partial<ITabsPanelProps> = {
        tabWidth: 100,
        tabHeight: 40,
        tabOrientation: 'horizontal',
        tabStyle: 'conventional'
    };

    private rootElementRef = React.createRef<HTMLDivElement>();
    private tabItemData = new Map<React.Key, ITabItemBaseProps>();
    private tabsIterator: ListIterator<React.Key>;
    private isComponentUnmounted = false;

    private updateComponent = debounce(() => {
        const { activeTab } = this.state;
        const newKeys = Array.from(this.tabItemData.keys());
        this.tabsIterator.updateList(newKeys);
        if (!activeTab) {
            this.tabsIterator.next();
            return;
        } else if (activeTab && !newKeys.includes(activeTab)) {
            this.tabsIterator.reset();
            this.tabsIterator.next();
            return;
        }
        this.forceUpdate();

        this.tabsIterator.find(key => key === activeTab);
    }, 100, this);

    private getProviderProps = memoizeFunction(() => ({ addItem: this.addItem, removeItem: this.removeItem }));

    private canSelectItem = (item: React.Key): boolean => {
        if (this.tabItemData.has(item)) {
            return !(this.tabItemData.get(item)?.disabled);
        }
        return true;
    }

    static getDerivedStateFromProps(props: ITabsPanelProps) {
        const { activeTab } = props;
        if (activeTab) {
            return { activeTab };
        }
        return null;
    }

    constructor(props: ITabsPanelProps) {
        super(props);
        const { activeTab, defaultActiveTab = '' } = props;
        this.prepareTabItemData();
        const options = {
            canSelectItem: this.canSelectItem
        };

        const tabItemDataKeys = Array.from(this.tabItemData.keys());
        this.tabsIterator = new ListIterator(tabItemDataKeys, options);

        this.state = {
            activeTab: activeTab || defaultActiveTab,
            tabOverFlow: false
        };
    }

    componentDidUpdate(prevProps: ITabsPanelProps) {
        const { tabItems, children } = this.props;
        const isDimensionChanged = this.isDimensionChanged(prevProps);
        const isTabItemChanged = tabItems !== prevProps.tabItems;
        const prevPropsChildElementLength = React.Children.count(prevProps.children);
        const propsChildElementLength = React.Children.count(children);

        if (isDimensionChanged || isTabItemChanged || prevPropsChildElementLength !== propsChildElementLength) {
            const tabOverFlow = this.hasTabsOverflew();
            this.setState({
                tabOverFlow
            });
        }
        if (isTabItemChanged) {
            this.prepareTabItemData();
        }
        return null;
    }

    componentDidMount() {
        const tabOverFlow = this.hasTabsOverflew();
        this.tabsIterator.subscribeToOnChange(this.onChange);
        this.setState({
            tabOverFlow
        });
    }

    componentWillUnmount() {
        this.tabsIterator.unsubscribeToOnChange(this.onChange);
        this.isComponentUnmounted = true;
    }

    render() {
        const { tabOrientation, tabItems, className, initialTabData } = this.props;
        return (
            <TabPanelWrapper className={className} ref={this.rootElementRef} tabOrientation={tabOrientation!}>
                {tabItems ? null : this.inferTabsFromChildren()}
                {this.renderTabHeaderContainer()}
                <TabDataProvider initialTabData={initialTabData}>
                    {this.renderTabContentContainer()}
                </TabDataProvider>
            </TabPanelWrapper>
        );
    }

    private prepareTabItemData() {
        const { tabItems } = this.props;
        if (tabItems) {
            this.tabItemData = new Map();
            tabItems.forEach(props => {
                this.tabItemData.set(props.id, props);
            });
            this.updateComponent();
        }

    }

    private inferTabsFromChildren() {
        const { children } = this.props;
        return (
            <TabContext.Provider value={this.getProviderProps()}>
                {children}
            </TabContext.Provider>
        );
    }

    private addItem = (props: ITabItemProps) => {
        const { id } = props;
        this.tabItemData.set(id, props);
        this.updateComponent();
    }

    private removeItem = (id: React.Key) => {
        if (this.isComponentUnmounted) {
            return;
        }
        this.tabItemData.delete(id);
        this.updateComponent();
    }

    private renderTabHeaderContainer() {
        const { tabOverFlow, activeTab } = this.state;
        const { tabOrientation, tabStyle,
            tabWidth = 0, tabHeight = 0, enableAddTab, remoteHeaderContainerRef, onAddTab } = this.props;
        const tabItems = this.tabsIterator.getAllValues();
        const childElementLength = tabItems.length;
        if (childElementLength === 0 || !activeTab) {
            return null;
        }
        const component = (
            <TabHeaderContainer
                tabOverflow={tabOverFlow}
                activeTab={activeTab}
                tabOrientation={tabOrientation!}
                tabStyle={tabStyle!}
                remoteHeaderContainerRef={remoteHeaderContainerRef}
                childCount={childElementLength}
                tabWidth={tabWidth}
                tabHeight={tabHeight}
                tabItemData={new Map(this.tabItemData)}
                tabsIterator={this.tabsIterator}
                enableAddTab={enableAddTab}
                onAddTab={onAddTab}
                onChange={this.onChange} />
        );
        if (remoteHeaderContainerRef && remoteHeaderContainerRef.current) {
            return createPortal(component, remoteHeaderContainerRef.current);
        }
        return component;
    }

    private renderTabContentContainer() {
        const { tabOrientation, tabStyle, remoteHeaderContainerRef } = this.props;
        const { activeTab, tabOverFlow } = this.state;
        const props = this.tabItemData.get(activeTab);
        const tabItems = this.tabsIterator.getAllValues();
        if (tabItems.length === 0 || !activeTab) {
            return null;
        }
        return (
            <TabContentContainer
                tabOverflow={tabOverFlow}
                tabOrientation={tabOrientation!}
                tabStyle={tabStyle!}
                remoteHeaderContainerRef={remoteHeaderContainerRef}
                tabIndex={0}>
                <TabIdProvider id={activeTab}>
                    {this.renderTabContent(props)}
                </TabIdProvider>
            </TabContentContainer>
        );
    }

    private renderTabContent(itemProps?: ITabItemBaseProps) {
        const { onRenderItem } = this.props;
        if (itemProps?.onRenderItem) {
            return itemProps.onRenderItem();
        }
        if (onRenderItem) {
            return onRenderItem();
        }
        return itemProps?.children || null;
    }

    private hasTabsOverflew(): boolean {
        const { tabWidth, tabHeight, children, tabOrientation, tabItems } = this.props;
        const childElementLength = React.Children.count(children) || tabItems?.length;
        const { current } = this.rootElementRef;
        if (isHorizontal(tabOrientation!)) {
            return (clamp(tabWidth!, 100, 300) * childElementLength!) > current!.scrollWidth;
        }
        return (clamp(tabHeight!, 45, 100)! * childElementLength!) > current!.scrollHeight;
    }

    private isDimensionChanged(prevProps: ITabsPanelProps): boolean {
        const { tabOrientation } = this.props;
        return isHorizontal(tabOrientation)
            ? (this.props.tabWidth !== prevProps.tabWidth)
            : (this.props.tabHeight !== prevProps.tabHeight);
    }

    private onChange = (tabKey: React.Key) => {

        const { onChange, activeTab } = this.props;
        const isInUncontrolledMode = (activeTab === undefined);

        if (onChange) {
            const activeTabDetails = {
                activeTab: tabKey,
                handled: isInUncontrolledMode
            };
            onChange(activeTabDetails);
        }

        if (isInUncontrolledMode) {
            this.setState({
                activeTab: tabKey
            });
        }

    }

}
